// src/components/surveys/questions/TextQuestion.tsx
import React from 'react';
import { SurveyQuestion } from '../../../types/survey.types';

interface TextQuestionProps {
  question: SurveyQuestion;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  showValidation?: boolean;
  error?: string;
  mode?: 'edit' | 'preview' | 'response';
}

const TextQuestion: React.FC<TextQuestionProps> = ({
  question,
  value = '',
  onChange,
  disabled = false,
  showValidation = false,
  error,
  mode = 'response',
}) => {
  const maxLength = question.validation?.maxLength || 500;
  const minLength = question.validation?.minLength || 0;
  const multiline = maxLength > 100;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  if (mode === 'edit') {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">Text input preview:</div>
        {multiline ? (
          <textarea
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50"
            rows={3}
            placeholder="Respondent's answer will appear here..."
            disabled
          />
        ) : (
          <input
            type="text"
            className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50"
            placeholder="Respondent's answer will appear here..."
            disabled
          />
        )}
        <div className="text-xs text-gray-400">
          {minLength > 0 && `Min: ${minLength} chars`}
          {minLength > 0 && maxLength && ' | '}
          {maxLength && `Max: ${maxLength} chars`}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {multiline ? (
        <textarea
          value={value}
          onChange={handleChange}
          disabled={disabled}
          maxLength={maxLength}
          rows={4}
          placeholder="Enter your answer..."
          className={`w-full p-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            error
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-200 focus:border-indigo-500'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={handleChange}
          disabled={disabled}
          maxLength={maxLength}
          placeholder="Enter your answer..."
          className={`w-full p-3 border-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            error
              ? 'border-red-300 focus:border-red-500'
              : 'border-gray-200 focus:border-indigo-500'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
        />
      )}
      <div className="flex justify-between text-xs text-gray-400">
        <span>
          {minLength > 0 && value.length < minLength && (
            <span className="text-orange-500">
              {minLength - value.length} more characters required
            </span>
          )}
        </span>
        <span>
          {value.length}/{maxLength}
        </span>
      </div>
      {showValidation && error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default TextQuestion;
